/**
 * MaintenancePanel — co GitLab na uzlu zabírá a co v něm ACM ovládá.
 *
 * Filip 2026-08-24: „v UI to nevidím jako volby v ovládání gitlabu, ani to
 * nepíše, kolik to zabírá." Proto vlastní záložka, ne přepínače schované
 * v modalu nad řádkem instance.
 *
 * Čte `gitlab.storage_usage` (místo) a `gitlab.settings_get` (co je nastaveno),
 * zapisuje `gitlab.settings_apply` a spouští `gitlab.maintenance` — tedy tytéž
 * tooly, které po nasazení jedou samy.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Archive, HardDrive, Trash2 } from 'lucide-react';
import { api } from '../../../lib/api';
import { Badge, Button, Input } from '../../../components/ui';
import type { WidgetRenderProps } from '../../../widgets/types';

interface UsageResp {
  ok: boolean;
  disk?: { total: number; free: number; used_pct: number } | null;
  backups?: {
    bytes: number;
    instances: Array<{
      name: string;
      status: string;
      version: string | null;
      backups_count: number | null;
      backups_bytes: number | null;
      measurable: boolean;
    }>;
  };
  images?: {
    count: number;
    bytes: number;
    reclaimable_bytes: number;
    keep_images?: number;
    images: Array<{
      image: string;
      size_bytes: number | null;
      in_use: boolean;
      prunable: boolean;
      kept_reason?: string | null;
    }>;
  };
  total_bytes?: number;
}

interface SettingsResp {
  ok: boolean;
  error?: string;
  signup_enabled?: boolean;
  sso_only?: boolean;
  edition_title?: string | null;
  maintenance?: { keep_backups: number; prune_images: boolean; keep_images: number };
}

function human(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  const u = ['B', 'kB', 'MB', 'GB', 'TB'];
  let v = bytes, i = 0;
  while (v >= 1000 && i < u.length - 1) {
    v /= 1000;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

export function MaintenancePanel(_: WidgetRenderProps = {} as WidgetRenderProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState<{
    signup: boolean;
    ssoOnly: boolean;
    title: string;
    keepBackups: number;
    pruneImages: boolean;
  } | null>(null);

  const usage = useQuery<UsageResp>({
    queryKey: ['gitlab-usage'],
    queryFn: () => api.mcp<UsageResp>('gitlab.storage_usage', {}, { silent: true }),
    staleTime: 60_000,
  });

  const settings = useQuery<SettingsResp>({
    queryKey: ['gitlab-settings'],
    queryFn: async () => {
      const r = await api.mcp<SettingsResp>('gitlab.settings_get', {}, { silent: true });
      if (r?.ok) {
        setForm({
          signup: r.signup_enabled === true,
          ssoOnly: r.sso_only === true,
          title: r.edition_title ?? '',
          keepBackups: r.maintenance?.keep_backups ?? 5,
          pruneImages: r.maintenance?.prune_images !== false,
        });
      }
      return r;
    },
  });

  const save = useMutation({
    mutationFn: () =>
      api.mcp<{ ok?: boolean }>('gitlab.settings_apply', {
        signup_enabled: form?.signup ?? false,
        sso_only: form?.ssoOnly ?? true,
        ...(form?.title ? { edition_title: form.title } : {}),
        logo: false,
        maintenance: {
          keep_backups: form?.keepBackups ?? 5,
          prune_images: form?.pruneImages ?? true,
        },
      }, { label: t('gitlab.settings.saveLabel', { name: 'GitLab' }) }),
    onSuccess: (r) => {
      if (r?.ok === false) return;
      toast.success(t('gitlab.settings.saved'));
      qc.invalidateQueries({ queryKey: ['gitlab-settings'] });
    },
  });

  const clean = useMutation({
    mutationFn: (dry: boolean) =>
      api.mcp<{ ok?: boolean; images?: { removed?: string[] }; backups?: unknown[] }>(
        'gitlab.maintenance',
        dry ? { dry_run: true } : {},
        { label: t('gitlab.settings.cleanLabel') },
      ),
    onSuccess: (r, dry) => {
      if (r?.ok === false) return;
      const count = r?.images?.removed?.length ?? 0;
      toast.success(
        dry
          ? t('gitlab.settings.cleanPreview', { count })
          : t('gitlab.settings.cleanOk', { count }),
      );
      if (!dry) {
        qc.invalidateQueries({ queryKey: ['gitlab-usage'] });
      }
    },
  });

  const u = usage.data;
  const disk = u?.disk ?? null;

  return (
    <div className="p-3 space-y-4">
      {/* ── Kolik to zabírá ─────────────────────────────────────────── */}
      <div>
        <div className="text-xs text-zinc-400 mb-2 flex items-center gap-1.5">
          <HardDrive size={12} /> {t('gitlab.settings.usageTitle')}
        </div>
        {usage.isLoading
          ? <div className="text-sm text-zinc-500">{t('common.loading')}</div>
          : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div className="rounded border border-zinc-800 px-2 py-1.5">
                <div className="text-zinc-500">{t('gitlab.settings.usageBackups')}</div>
                <div className="font-mono text-zinc-100 text-sm">{human(u?.backups?.bytes)}</div>
              </div>
              <div className="rounded border border-zinc-800 px-2 py-1.5">
                <div className="text-zinc-500">{t('gitlab.settings.usageImages')}</div>
                <div className="font-mono text-zinc-100 text-sm">{human(u?.images?.bytes)}</div>
                <div className="text-[10px] text-zinc-500">
                  {t('gitlab.settings.usageImagesCount', { count: u?.images?.count ?? 0 })}
                </div>
              </div>
              <div className="rounded border border-zinc-800 px-2 py-1.5">
                <div className="text-zinc-500">{t('gitlab.settings.usageReclaimable')}</div>
                <div className="font-mono text-emerald-300 text-sm">
                  {human(u?.images?.reclaimable_bytes)}
                </div>
              </div>
              <div className="rounded border border-zinc-800 px-2 py-1.5">
                <div className="text-zinc-500">{t('gitlab.settings.usageDisk')}</div>
                <div className="font-mono text-zinc-100 text-sm">
                  {disk ? human(disk.free) : '—'}
                </div>
                {disk && (
                  <div className="text-[10px] text-zinc-500">
                    {t('gitlab.settings.usageDiskUsed', { pct: disk.used_pct })}
                  </div>
                )}
              </div>
            </div>
          )}

        {(u?.backups?.instances?.length ?? 0) > 0 && (
          <div className="mt-2 space-y-1">
            {u!.backups!.instances.map((i) => (
              <div key={i.name} className="flex items-center gap-2 text-[11px] text-zinc-400">
                <span className="font-mono text-zinc-300">{i.name}</span>
                {i.measurable
                  ? (
                    <span>
                      {t('gitlab.settings.usageInstanceBackups', {
                        count: i.backups_count ?? 0,
                        size: human(i.backups_bytes),
                      })}
                    </span>
                  )
                  : <span className="text-zinc-600">{t('gitlab.settings.usageNotRunning')}</span>}
              </div>
            ))}
          </div>
        )}

        {(u?.images?.images?.length ?? 0) > 0 && (
          <div className="mt-2 space-y-1">
            {u!.images!.images.map((i) => (
              <div key={i.image} className="flex items-center gap-2 text-[11px]">
                <span className="font-mono text-zinc-300">{i.image}</span>
                <span className="text-zinc-500">{human(i.size_bytes)}</span>
                {i.in_use
                  ? <Badge variant="green">{t('gitlab.settings.imageInUse')}</Badge>
                  : i.prunable
                  ? <Badge variant="yellow">{t('gitlab.settings.imagePrunable')}</Badge>
                  : (
                    <Badge variant="gray">
                      {i.kept_reason === 'keep_images'
                        ? t('gitlab.settings.imageKeptReserve')
                        : t('gitlab.settings.imageKept')}
                    </Badge>
                  )}
              </div>
            ))}
          </div>
        )}

        <div className="mt-2 flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            loading={clean.isPending}
            onClick={() => clean.mutate(false)}
          >
            <Trash2 size={13} /> {t('gitlab.settings.cleanNow')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            loading={clean.isPending}
            onClick={() => clean.mutate(true)}
          >
            <Archive size={13} /> {t('gitlab.settings.cleanDryRun')}
          </Button>
        </div>
      </div>

      {/* ── Co v GitLabu ovládá ACM ─────────────────────────────────── */}
      <div>
        <div className="text-xs text-zinc-400 mb-2">{t('gitlab.settings.controlTitle')}</div>
        {settings.isLoading || !form
          ? <div className="text-sm text-zinc-500">{t('common.loading')}</div>
          : settings.data?.ok === false
          ? <div className="text-sm text-red-300">{settings.data.error}</div>
          : (
            <div className="text-sm">
              {([
                ['signup', 'signup', 'signupHelp'],
                ['ssoOnly', 'ssoOnly', 'ssoOnlyHelp'],
                ['pruneImages', 'pruneImages', 'pruneImagesHelp'],
              ] as const).map(([key, label, help]) => (
                <div
                  key={key}
                  className="flex items-start justify-between gap-3 py-2 border-b border-zinc-900"
                >
                  <div>
                    <div className="text-zinc-200">{t(`gitlab.settings.${label}`)}</div>
                    <div className="text-[11px] text-zinc-500">{t(`gitlab.settings.${help}`)}</div>
                  </div>
                  <input
                    type="checkbox"
                    className="accent-blue-500 mt-1"
                    checked={form[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                  />
                </div>
              ))}
              <div className="flex items-start justify-between gap-3 py-2 border-b border-zinc-900">
                <div>
                  <div className="text-zinc-200">{t('gitlab.settings.keepBackups')}</div>
                  <div className="text-[11px] text-zinc-500">
                    {t('gitlab.settings.keepBackupsHelp')}
                  </div>
                </div>
                <input
                  type="number"
                  min={0}
                  max={50}
                  className="w-16 bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-sm text-right"
                  value={form.keepBackups}
                  onChange={(e) => setForm({ ...form, keepBackups: Number(e.target.value) })}
                />
              </div>
              <div className="py-2">
                <div className="text-zinc-200">{t('gitlab.settings.editionTitle')}</div>
                <Input
                  className="mt-1"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="GitLab ACM Edition"
                />
              </div>
              <div className="pt-2 flex justify-end">
                <Button variant="primary" size="sm" loading={save.isPending} onClick={() => save.mutate()}>
                  {t('common.save')}
                </Button>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}
